import { ArrowLeft } from "lucide-react";
import * as React from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { TourButton } from "@/components/layout/tour";
import { cn } from "@/lib/utils";

/**
 * Where the page on screen was opened from, when that is a page of its own: the app shell sets it
 * from the route map (`settingsBackLink` in `lib/nav.ts`) for the pages behind Settings, and
 * `PageHeader` draws it. A screen does not pass it itself, so a page moved behind Settings gets its
 * way back without an edit, and one moved out loses it.
 */
export const PageBackContext = React.createContext<{ to: string; label: string } | null>(null);

/**
 * The frame's top pane: the band over the page, between the sidebar and the phone's rail, on the
 * frame the two of them sit on (`app-shell.tsx`). The shell owns the place and a screen fills it
 * with `FrameTop`: the Phone page puts the phone's own strip there, so the sidebar, that strip
 * and the rail read as one frame around the page. A screen that puts nothing there leaves a thin
 * band of frame over the page.
 */
export const FrameTopContext = React.createContext<HTMLElement | null>(null);

/** What a screen has for the frame's top pane. Outside the shell (the state gallery) there is no
 *  such place, and it is drawn where the screen put it. */
export function FrameTop({ children }: { children: React.ReactNode }) {
  const place = React.useContext(FrameTopContext);
  return place ? createPortal(children, place) : <>{children}</>;
}

/**
 * Every screen opens the same way: title on the left, primary action on the right, optional
 * one-line description. Consistency here is most of what makes a console feel like one product
 * rather than a pile of pages.
 *
 * A screen that has a product tour (`useTour`) gets "Take the tour" beside its action, without
 * passing anything: the button reads the tour the screen registered.
 */
export function PageHeader({
  title, description, action, className, tour,
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
  /** Names the title and its description for a step of the screen's tour (`data-tour`). */
  tour?: string;
}) {
  const back = React.useContext(PageBackContext);
  return (
    <>
      {/* The link a record's page carries back to its list (`← Campaigns`), drawn the same way. */}
      {back && (
        <Link
          to={back.to}
          className="mb-3 inline-flex items-center gap-1 text-xs text-text-muted hover:text-text"
        >
          <ArrowLeft className="size-3.5" aria-hidden />
          {back.label}
        </Link>
      )}
      <div className={cn("flex items-start justify-between gap-4 pb-4", className)}>
        <div className="min-w-0 space-y-1" data-tour={tour}>
          <h1 className="text-xl font-semibold tracking-tight text-text">{title}</h1>
          {description && <p className="text-sm text-text-muted">{description}</p>}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <TourButton />
          {action}
        </div>
      </div>
    </>
  );
}

/**
 * What a screen's content sits in: the whole width of the content area, with the page's own
 * margin around it. It was held to 80rem and centred, so on a window wider than that every list
 * and every settings screen stood between two empty strips of the sheet while its table ran out
 * of room for columns. What reads better narrow is held where it is, inside the page (a form's
 * `max-w-md`), never by narrowing the page: the reports held themselves to 1440px for a while
 * and were the one screen with a gutter on a wide monitor.
 */
export function Page({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("w-full px-6 py-6", className)} {...props} />;
}

/** A filter/search strip above a table. */
export function Toolbar({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("flex flex-wrap items-center gap-2 border-b border-border px-3 py-2", className)}
      {...props}
    />
  );
}
